<template>
  <div
    class="modal-canvas__stage"
    :class="{ 'modal-canvas__stage--resizing': isResizing }"
    ref="modalStage"
    @scroll="onModalScrollThrottled"
  >
    <EditorMenu
      ref="editorMenu"
      v-if="mode === 'EDIT'"
      :scale="scale"
      :trigger-element="$el"
      :trigger-padding="0"
      :positions="['top']"
      box-name="editor"
      :box-padding="{ horizontal: 0 }"
      id-prefix="modal-canvas-toolbar"
    >
      <ModalCanvasToolbar
        class="modal-canvas__toolbar"
      />
    </EditorMenu>
    <div
      class="modal-canvas__zoom-spacer"
      :style="zoomSpacerStyle"
    />
    <div
      ref="modalCanvasInnerWrap"
      class="modal-canvas__inner-wrap"
      :class="{
        'modal-canvas__inner-wrap--mystery': pageIsMysteryTheme,
        'modal-canvas__inner-wrap--view-mode': mode === 'VIEW' || editorViewModePreview,
      }"
      :style="{
        height: `${modalCanvasHeight}px`,
        width: `${modalCanvasWidth}px`,
        ...zoomContentStyle,
      }"
    >
      <GridLayout
        v-if="modalCanvas && modalCanvas.id"
        ref="gridLayout"
        class="modal-canvas"
        :class="{
          'modal-canvas--placeholder-hidden': placeholderHidden,
        }"
        :col-num="colNum"
        :row-height="rowHeight"
        :is-draggable="mode === 'EDIT'"
        :is-resizable="mode === 'EDIT'"
        :margin="gridMargin"
        :layout="copiedModuleLayout"
        :vertical-compact="false"
        :prevent-collision="preventCollision"
        :max-rows="maxRows"
        :auto-size="true"
        :use-css-transforms="false"
        :allow-overlap="gridIsDisabled"
        :allow-out-of-bounds="gridIsDisabled"
        :use-placeholder="!gridIsDisabled"
        :snap-grid="snapGrid"
        :expand-height-to-max-rows="expandHeightToMaxRows"
        @layout-updated="onGridLayoutUpdated($event, modules, 'modalModules')"
      >
        <NebulaButton
          class="modal-canvas__x-button"
          icon-left="x"
          type="flat"
          size="s"
          :aria-label="$t('Close modal')"
          @click="onClickClose"
        />
        <template
          v-for="{ mod, layout } in copiedModules"
          :key="mod.id"
        >
          <div
            v-if="mode === 'EDIT'"
            v-show="mod.id === activeModule"
            ref="gridItemBeforeContainers"
            :id="`grid-item-before-container-${mod.id}`"
            class="grid-canvas__grid-item-before-container"
          />
          <GridItem
            :x="layout.x"
            :y="layout.y"
            :z="moduleZIndexes[mod.id]"
            :w="layout.w"
            :h="layout.h"
            :r="layout.r"
            :i="mod.id"
            :scale="scaleZoomed"
            @move="onMove"
            @resize="onResize"
            @rotation-start="busEmit('module:rotation-start', { id: mod.id, ...$event })"
            @rotation-change="busEmit('module:rotation-change', { id: mod.id, ...$event })"
            @rotation-end="busEmit('module:rotation-end', { id: mod.id, ...$event })"
            :is-resizable="!isLocked(mod.id) && mode === 'EDIT'"
            :is-resizable-y="!isLocked(mod.id) && !autoSizeShrinkEnabled(mod, 'modalModules')"
            :is-draggable="!isLocked(mod.id) && mode === 'EDIT'"
            :allow-rotation="activeModule === mod.id && mode === 'EDIT'
              && gridIsDisabled && !isLocked(mod.id)"
            ref="gridItems"
            :id="mod.id"
            :drag-ignore-from="EDITOR_MODULE_IGNORE_DRAG_SELECTOR"
            :class="[
              {
                'vue-grid-item--active': mod.id === activeModule,
                'vue-grid-item--preview': mod.type === 'preview',
              },
            ]"
          >
            <ModuleWrapper
              :active="mod.id === activeModule"
              :mode="mode"
              :module-data="mod"
              :module-name="moduleTypeToLabel(mod.type)"
              canvas-name="modalModules"
              :scale="scaleZoomed"
              ref="moduleWrappers"
              :id="mod.id"
              :get-after-module-container-ref="getAfterModuleContainerRef"
              :module-animations-enabled="moduleAnimationsEnabled"
            >
              <component
                :is="moduleTypeToComponent(mod.type)"
                :id="mod.id"
                :active="mod.id === activeModule"
                :mode="mode"
                :module-data="mod"
                :layout-data="layout"
                :scale="scaleZoomed"
                canvas-name="modalModules"
                :get-before-module-container-ref="getBeforeModuleContainerRef"
                :page="page"
              />
              <template #module-options>
                <component
                  :is="moduleTypeToOptions(mod.type)"
                  :id="mod.id"
                  :module-data="mod"
                  canvas-name="modalModules"
                />
              </template>
            </ModuleWrapper>
            <template v-if="mode === 'EDIT' && !isLocked(mod.id)">
              <GridItemDragHandle
                position="top"
                :mod-type="mod.type"
              />
              <GridItemDragHandle
                position="end"
                :mod-type="mod.type"
              />
              <GridItemDragHandle
                position="bottom"
                :mod-type="mod.type"
              />
              <GridItemDragHandle
                position="start"
                :mod-type="mod.type"
              />
            </template>
          </GridItem>
          <div
            v-if="mode === 'EDIT'"
            v-show="mod.id === activeModule"
            ref="gridItemAfterContainers"
            :id="`grid-item-after-container-${mod.id}`"
            class="grid-canvas__grid-item-after-container"
          />
        </template>
        <ResizeVertical
          v-if="mode === 'EDIT'"
          class="modal-canvas__resize-vertical"
          :id="currentModalCanvasId"
          :style="resizeVerticalStyle"
          @resize-start="onResizeStart($event)"
          @resize-move="onResizeMove($event)"
          @resize-end="onResizeEnd($event)"
          @resize-up="onResizeByIncrement(resizeIncrement)"
          @resize-down="onResizeByIncrement(-(resizeIncrement))"
          @focus="resizeIsFocused = true"
          @blur="resizeIsFocused = false"
        />
      </GridLayout>
    </div>
    <div
      class="modal-canvas__click-target"
      @click="onClickClose($event, true)"
    />
  </div>
</template>

<script>
import { NebulaButton } from '@discoveryedu/nebula-components';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import interact from 'interactjs';
import { mapActions, mapState } from 'pinia';
import { GridLayout, GridItem } from '@discoveryedu/vue-grid-layout';
import { get, throttle } from 'lodash-es';
import GridItemDragHandle from '@/components/GridItemDragHandle.vue';
import ModalCanvasToolbar from '@/components/ModalCanvasToolbar.vue';
import EditorMenu from '@/components/EditorMenu.vue';
import {
  isMobileOrTablet,
  moduleTypeToComponent,
  moduleTypeToOptions,
  moduleTypeToLabel,
} from '@/lib/utils';
import {
  EDITOR_CANVAS_NO_GRID_ROW_HEIGHT,
  EDITOR_CANVAS_NO_GRID_MARGIN,
  EDITOR_MODULE_IGNORE_DRAG_SELECTOR,
  MODAL_SNAP_GRID_CELL_WIDTH,
  MODAL_SNAP_GRID_CELL_HEIGHT,
  MODAL_SNAP_GRID_CELL_LARGE_WIDTH,
  MODAL_SNAP_GRID_CELL_LARGE_HEIGHT,
  MODAL_CANVAS_WIDTH,
  MODAL_CANVAS_HEIGHT,
  MODAL_CANVAS_PADDING,
  MODAL_CANVAS_PADDING_EDIT,
  MODAL_CANVAS_MIN_HEIGHT,
  MODAL_CANVAS_MAX_HEIGHT,
} from '@/lib/constants';
import ModuleWrapper from '@/components/ModuleWrapper.vue';
import ResizeVertical from '@/components/ui-elements/ResizeVertical.vue';
import modules from '@/components/modules/index';
import * as types from '@/lib/constants/store';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import GridBackground from '@/components/GridBackground.vue';
import { bus } from '@/lib/eventBus';
import canvas from '@/mixins/canvas';
import autoSizeEnabled from '@/mixins/autoSizeEnabled';
import { createTopZPosition, formatModuleLayoutForGrid } from '@/lib/utils/grid';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';

const nebulaSpace8x = parseInt(nebulaTokens.space['8x'], 10);

export default {
  name: 'ModalCanvas',
  components: {
    GridItem,
    GridLayout,
    ModuleWrapper,
    NebulaButton,
    GridBackground,
    CometBlockMessage,
    GridItemDragHandle,
    ModalCanvasToolbar,
    EditorMenu,
    ResizeVertical,
    ...modules, // merge dynamic components
  },
  mixins: [canvas, autoSizeEnabled],
  computed: {
    ...mapState(useEditorStore, {
      connectedUser: 'currentUserConnection',
    }),
    ...mapState(useEditorStore, [
      'currentPage',
      'pagesSorted',
      'currentlyEditing',
      'getModulesByModalIdOrderedByZPosition',
      'getOrderedModulesByModalId',
      'isLocked',
      'isCurrentPageHost',
      'draftType',
      'modalCanvases',
      'currentModalCanvasId',
      'draft',
      'editorDrawers',
      'editorViewModePreview',
      'session',
      'editorIsFullscreen',
    ]),
    ...mapState(useUserStore, [
      'isRtl',
    ]),
    page() {
      return this.pageId
        ? this.pagesSorted?.find((p) => p.id === this.pageId)
        : this.currentPage;
    },
    modalCanvas() {
      return this.modalCanvases.find((modal) => modal.id === this.currentModalCanvasId);
    },
    modalCanvasPadding() {
      return this.mode === 'EDIT' ? MODAL_CANVAS_PADDING_EDIT : MODAL_CANVAS_PADDING;
    },
    pageIsMysteryTheme() {
      return get(this.page, 'options.theme.name') === 'mystery-science';
    },
    gridIsDisabled() {
      return true;
    },
    copiedModuleLayout() {
      return this.copiedModules.map((item) => item.layout);
    },
    modulesOrderedByZPosition() {
      return this.getModulesByModalIdOrderedByZPosition(this.currentModalCanvasId);
    },
    modules() {
      return this.getOrderedModulesByModalId(this.currentModalCanvasId);
    },
    placeholderHidden() {
      // The placeholder module is always hidden in VIEW mode
      // and while dragging onto the canvas from the Add Module menu
      return this.mode === 'VIEW' || this.isDraggingFromAddModule;
    },
    maxRows() {
      return this.modalCanvasHeight;
    },
    colNum() {
      return this.modalCanvasWidth;
    },
    rowHeight() {
      return EDITOR_CANVAS_NO_GRID_ROW_HEIGHT;
    },
    gridMargin() {
      return [EDITOR_CANVAS_NO_GRID_MARGIN, EDITOR_CANVAS_NO_GRID_MARGIN];
    },
    preventCollision() {
      return !this.gridIsDisabled;
    },
    moduleZIndexes() {
      if (!this.gridIsDisabled) return {};
      /*
        If the grid is disabled, then assign z-indexes to modules based
        on their current z_position.
      */
      return this.modulesOrderedByZPosition.reduce((accumulator, mod, modIndex) => {
        const zIndexes = accumulator;
        zIndexes[mod.id] = modIndex + 1;
        return zIndexes;
      }, {});
    },
    snapGrid() {
      return this.showSnapGrid ? {
        cellWidth: MODAL_SNAP_GRID_CELL_WIDTH,
        cellHeight: MODAL_SNAP_GRID_CELL_HEIGHT,
        cellLargeWidthCellCount: MODAL_SNAP_GRID_CELL_LARGE_WIDTH,
        cellLargeHeightCellCount: MODAL_SNAP_GRID_CELL_LARGE_HEIGHT,
        lineColor: nebulaTokens.color.platform.neutral['500'],
        showGrid: true,
      } : null;
    },
    initialScrollPercentLeft() {
      return this.isRtl ? -0.5 : 0.5;
    },
    modalCanvasHeight() {
      /*
        When resizing, return the local resized canvas height instead
        of the height in the app store.
      */
      return this.isResizing
        ? this.resizeData.updatedCanvasHeight
        : this.modalCanvas?.options?.height || MODAL_CANVAS_HEIGHT;
    },
    modalCanvasWidth() {
      return this.modalCanvas?.options?.width || MODAL_CANVAS_WIDTH;
    },
    isResizing() {
      return !!this.resizeData;
    },
    resizeVerticalStyle() {
      // Maintain original size regardless of zoom scale.
      const resizeHandleScale = 1 / this.scaleZoomed;
      const offset = 50 * this.scaleZoomed;
      /*
        Show the resize handle if mouse is within the boundaries of the
        canvas or directly below, if the resize handle is focused, or
        if this is a mobile device.
      */
      let showResizeHandle = this.resizeIsFocused || isMobileOrTablet();
      if (!showResizeHandle && this.zoomContentRect && this.mousePosition) {
        const {
          left, top, bottom, right,
        } = this.zoomContentRect;
        // Extend the trigger area below the resize handle
        const bottomExt = bottom + nebulaSpace8x;
        const { x, y } = this.mousePosition;
        showResizeHandle = x >= left
          && x <= right
          && y >= top
          && y <= bottomExt;
      }
      return {
        transform: `scale(${resizeHandleScale}) translate(-${offset}%, ${offset}%)`,
        opacity: showResizeHandle ? 1 : 0,
      };
    },
  },
  watch: {
    session() {
      this.$nextTick(() => {
        if (this.activeModule && !this.currentlyEditing(this.activeModule)) {
          this.activeModule = null;
        }
      });
    },
    modules: {
      handler() {
        // Update local modules with Pinia data after API calls
        this.updateCopiedModules();
        this[types.CACHE_GRID_MATRIX]({ columns: this.colNum, rows: this.rowNum });

        /*
          Anytime changes are made to the list of modules for the current page,
          the 'layout' prop for the GridLayout component will be updated with these
          changes.
          When two or more modules have overlapping coordinates or dimensions, we
          wait for the GridLayout component to resolve these conflicts internally.
          Then we get the resolved layout and push these changes back into Pinia
          and the SharedDocument so they propagate to other clients and are persisted
          to the server.
          Only the host user for the current page will commit resolved grid conflicts to avoid
          neverending cyclical updates between multiple clients.
        */
        if (this.isCurrentPageHost) {
          this.$nextTick(() => {
            const layout = get(this, '$refs.gridLayout.layout');
            if (layout) {
              this.onGridLayoutUpdated(layout, this.modules, 'modalModules', false);
            }
          });
        }
      },
      deep: true,
    },
    mode() {
      if (this.mode === 'VIEW') {
        this.activeModule = null;
      }
    },
    zoomLevel() {
      this.updateZoomContentStyle();
    },
    scaleZoomed() {
      this.emitScaleZoomChanged();
    },
    isRtl() {
      this.initScrollPercents();
      this.updateZoomContentStyle();
    },
    modalCanvasHeight() {
      if (this.isResizing) return;
      this.updateZoomContentStyle();
      this.resizeGridLayout();
    },
    modalCanvasWidth() {
      this.updateZoomContentStyle();
      this.resizeGridLayout();
    },
    resizeData() {
      if (!this.isResizing) return;
      this.updateZoomContentStyle();
    },
  },
  created() {
    // Capture Vue model instance so we don't lose context in arrow functions
    const vm = this;

    // Store a local copy of the state's modules so that the layout is able to mutate it
    this.updateCopiedModules();

    // Initialize a module to be previewed and/or added
    bus.on('initPlaceholderModule', (data) => {
      if (data.canvasName === 'modalModules') {
        this.placeholderModule.type = data.type;
        this.placeholderModule.name = data.name;
        this.placeholderModule.options = data.options;
        this.placeholderModule.layout = data.layout;
      }
    });

    bus.on('updateDraggableXY', (data) => {
      if (data.canvasName === 'modalModules') {
        vm.x_position = data.x;
        vm.y_position = data.y;
      }
    });

    bus.on('activeModule', (data) => {
      if (data.canvasName === 'modalModules') {
        vm.setActive(data.id);
        this[types.UPDATE_CURRENTLY_EDITING]({
          [data.id]: this.connectedUser?.id,
        });
      }
    });

    this.onModalScrollThrottled = throttle(() => {
      if (this.suppressScrollEvent) {
        this.suppressScrollEvent = false;
        return;
      }
      this.resetTopScrollPercent();
      this.resetLeftScrollPercent();
    }, 50);

    this.throttleUpdateZoomContentStyle = throttle(() => {
      this.updateZoomContentStyle();
    }, 5);

    this.updateMousePositionThrottled = throttle((event) => {
      this.updateMousePosition(event);
    }, 50);
  },
  data() {
    return {
      activeModule: null,
      copiedModules: [],
      placeholderModule: {
        // We don't need an ID property because it will be assigned in Pinia action
        type: String,
        name: String,
        layout: Object,
        options: Object,
      },
      isDraggingFromAddModule: false,
      tempId: '00000000',
      x_position: 0,
      y_position: 0,
      dropped: false,
      EDITOR_MODULE_IGNORE_DRAG_SELECTOR,
      zoomContentStyle: null,
      zoomSpacerStyle: null,
      scaleZoomed: 1,
      scale: 1,
      scrollPercentTop: 0,
      scrollPercentLeft: 0,
      suppressScrollEvent: false,
      initialScrollPercentTop: 0.5,
      canvasResizeObserver: null,
      canvasBorderWidth: 0,
      resizeData: null,
      resizeIncrement: 10,
      resizeIsFocused: false,
      zoomContentRect: null,
      mousePosition: null,
    };
  },
  props: {
    mode: {
      type: String,
      default: 'VIEW', // out of ['EDIT', 'VIEW']
    },
    zoomLevel: {
      type: Number,
      default: 100,
    },
    scrollContainer: {
      type: HTMLElement,
      default: null,
    },
    menuContainer: {
      type: HTMLElement, // used in gridLayout mixin
      default: null,
    },
    showSnapGrid: {
      type: Boolean,
    },
    expandHeightToMaxRows: {
      type: Boolean,
      default: false,
    },
    moduleAnimationsEnabled: {
      type: Boolean,
      default: true,
    },
    pageId: {
      type: String,
      default: null,
    },
  },
  emits: ['scale-zoomed-changed'],
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODULE,
      types.CACHE_GRID_MATRIX,
      types.CLOSE_MODAL_CANVAS,
      types.UPDATE_CURRENTLY_EDITING,
      types.RESIZE_MODAL_CANVAS,
    ]),
    busEmit(...args) {
      bus.emit(...args);
    },
    moduleTypeToComponent,
    moduleTypeToOptions,
    moduleTypeToLabel,
    onBodyClick({ target }) {
      this.onGridElementClicked(target);
    },
    onClickClose(event, viewOnly = false) {
      if (
        this.currentModalCanvasId
        && (
          (viewOnly && this.mode === 'VIEW')
          || !viewOnly
        )) {
        this[types.CLOSE_MODAL_CANVAS]();
      }
    },
    updateCopiedModules() {
      this.copiedModules = this.modules.map((mod) => ({
        mod,
        layout: formatModuleLayoutForGrid(mod),
      }));
    },
    onMove(moduleId) {
      this.resetDragging();
      bus.emit('module:move', moduleId);
    },
    onResize(moduleId) {
      this.resetDragging();
      bus.emit('module:resize', moduleId);
    },
    resetDragging() {
      this.isDraggingFromAddModule = false;
    },
    setActive(id) {
      this.activeModule = id;
    },
    async addModuleToState(mod) {
      const newModuleId = await this[types.ADD_MODULE]({
        canvasName: 'modalModules',
        payload: mod,
      });

      bus.emit('activeModule', {
        canvasName: 'modalModules',
        id: newModuleId,
      });

      this.dropped = true;
    },
    initDropZone() {
      const vm = this; // Current vue model, so we don't lose context in arrow functions
      let canvasEntered = null; // flag for drop area
      // Initialize drop zone and set up listeners
      const dropElement = this.$refs.modalCanvasInnerWrap;
      if (interact.isSet(dropElement)) {
        interact(dropElement).unset();
      }
      interact(dropElement).dropzone({
        accept: '.add-module-drawer .add-module__list-item--modalModules',
        ondropactivate() {
          vm.isDraggingFromAddModule = true;
        },
        ondragenter() {
          // switch the enter flag to true and initalize a position on the grid for the preview
          canvasEntered = 'modalModules';
          const placeholderLayout = {
            x_position: vm.x_position,
            y_position: vm.y_position,
            width: vm.placeholderModule.layout.width,
            height: vm.placeholderModule.layout.height,
          };
          vm.placeholderModule.layout = placeholderLayout;

          // Create a temporary module so the user can preview placement on grid
          const previewModule = vm.createPreviewModule(placeholderLayout);

          // When the preview begins, create a temporary module in the copiedModules list
          // to render the preview in the gridLayout
          bus.emit('beginPreview', { canvasEntered });
          vm.copiedModules.push({
            mod: previewModule,
            layout: formatModuleLayoutForGrid(previewModule),
          });
        },
        ondragleave() {
          // When the preview ends, delete the temporary module from the copiedModules list
          canvasEntered = null;
          vm.copiedModules = vm.copiedModules.filter((item) => item.mod.type !== 'preview');
        },
        ondrop() {
          // When the floating module is dropped, create a new module on the preview's position
          // and send it to the state
          vm.placeholderModule.layout = {
            x_position: vm.x_position,
            y_position: vm.y_position,
            height: vm.placeholderModule.layout.height,
            width: vm.placeholderModule.layout.width,
          };

          if (vm.gridIsDisabled) {
            vm.placeholderModule.layout.z_position = createTopZPosition(
              vm.modulesOrderedByZPosition,
            );
          }

          vm.addModuleToState(vm.placeholderModule);
          canvasEntered = null;
        },
      });
    },
    createPreviewModule(layout) {
      // Stub object
      return {
        id: this.tempId, // Temporary id
        type: 'preview',
        name: this.$t('Preview'),
        options: {},
        layout,
      };
    },
    emitScaleZoomChanged() {
      this.$emit('scale-zoomed-changed', this.scaleZoomed);
    },
    updateZoomContentStyle() {
      const { modalStage } = this.$refs;
      if (!modalStage) return;
      const canvasPadding = this.modalCanvasPadding * 2;
      const borderWidth = this.canvasBorderWidth * 2;
      const stageWidth = modalStage.offsetWidth;
      const stageHeight = modalStage.offsetHeight;
      const canvasHeightWithBorder = this.modalCanvasHeight + borderWidth;
      const canvasWidthWithBorder = this.modalCanvasWidth + borderWidth;
      const heightRatio = (stageHeight - canvasPadding) / canvasHeightWithBorder;
      const widthRatio = (stageWidth - canvasPadding) / canvasWidthWithBorder;
      let scale = Math.min(widthRatio, heightRatio);
      if (scale > 1) scale = 1;
      this.scaleZoomed = (this.zoomLevel / 100) * scale;

      const scaledCanvasHeight = this.modalCanvasHeight * this.scaleZoomed;
      const scaledCanvasWidth = this.modalCanvasWidth * this.scaleZoomed;
      const scaledBorderWidth = borderWidth * this.scaleZoomed;
      const scaledTotalWidth = scaledCanvasWidth + scaledBorderWidth;
      const scaledTotalHeight = scaledCanvasHeight + scaledBorderWidth;

      const canvasCenteredX = stageWidth >= scaledTotalWidth;
      const canvasCenteredY = stageHeight >= scaledTotalHeight;

      const translateX = (canvasCenteredX
        ? (stageWidth - scaledTotalWidth) / 2
        : this.modalCanvasPadding) * (this.isRtl ? -1 : 1);

      const translateY = canvasCenteredY
        ? (stageHeight - scaledTotalHeight) / 2
        : this.modalCanvasPadding;

      this.zoomContentStyle = {
        transform: `translate(${translateX}px, ${translateY}px) scale(${this.scaleZoomed})`,
      };

      let spacerTranslateX = 0;
      let spacerTranslateY = 0;
      let spacerWidth = 1;
      let spacerHeight = 1;
      if (!canvasCenteredX) {
        spacerWidth = this.modalCanvasPadding;
        spacerTranslateX = translateX + (scaledTotalWidth * (this.isRtl ? -1 : 1));
      }
      if (!canvasCenteredY) {
        spacerHeight = this.modalCanvasPadding;
        spacerTranslateY = translateY + scaledTotalHeight;
      }
      this.zoomSpacerStyle = {
        height: `${spacerHeight}px`,
        width: `${spacerWidth}px`,
        transform: `translate(${spacerTranslateX}px, ${spacerTranslateY}px)`,
      };

      this.updateZoomScrollbars();
      bus.emit('editor:zoom-changed', this.scaleZoomed);

      this.$nextTick(() => {
        const { modalCanvasInnerWrap } = this.$refs;
        if (modalCanvasInnerWrap) {
          this.zoomContentRect = modalCanvasInnerWrap.getBoundingClientRect();
        }
      });
    },
    updateMousePosition(event) {
      this.mousePosition = {
        x: event.clientX,
        y: event.clientY,
      };
    },
    initScrollPercents() {
      this.scrollPercentTop = this.initialScrollPercentTop;
      this.scrollPercentLeft = this.initialScrollPercentLeft;
    },
    resetTopScrollPercent() {
      const { modalStage } = this.$refs;
      if (!modalStage) return;
      this.scrollPercentTop = modalStage.scrollHeight > modalStage.clientHeight
        ? modalStage.scrollTop / (modalStage.scrollHeight - modalStage.clientHeight)
        : this.initialScrollPercentTop;
    },
    resetLeftScrollPercent() {
      const { modalStage } = this.$refs;
      if (!modalStage) return;
      this.scrollPercentLeft = modalStage.scrollWidth > modalStage.clientWidth
        ? modalStage.scrollLeft / (modalStage.scrollWidth - modalStage.clientWidth)
        : this.initialScrollPercentLeft;
    },
    updateZoomScrollbars() {
      this.$nextTick(() => {
        /*
          HACK: In Chrome, sometimes the 'scrollHeight' of a div doesn't update after a child
          element has been scaled larger using 'transform: scale()'. This hack forces the container
          div to redraw itself and update the 'scrollHeight' and scroll bars accordingly. We don't
          want to use Vue to rerender the component as that would cause any currently playing media
          within to be be reset.
        */
        const modalStageRef = this.$refs?.modalStage;
        if (modalStageRef) {
          const { display } = modalStageRef.style;
          modalStageRef.style.display = 'none';
          this.modalStageOffsetHeight = modalStageRef.offsetHeight;
          modalStageRef.style.display = display;
        }

        this.$nextTick(() => {
          /*
            After we force the container to redraw its scroll bars, then calculate
            current scroll positions.
          */
          const { modalStage } = this.$refs;
          if (!modalStage) return;
          if (modalStage.scrollHeight > modalStage.clientHeight) {
            modalStage.scrollTop = this.scrollPercentTop
              * (modalStage.scrollHeight - modalStage.clientHeight);
            this.suppressScrollEvent = true;
          } else {
            this.resetTopScrollPercent();
          }
          if (modalStage.scrollWidth > modalStage.clientWidth) {
            modalStage.scrollLeft = this.scrollPercentLeft
              * (modalStage.scrollWidth - modalStage.clientWidth);
            this.suppressScrollEvent = true;
          } else {
            this.resetLeftScrollPercent();
          }
        });
      });
    },
    createCanvasResizeObserver() {
      if (!window.ResizeObserver) return;
      this.destroyCanvasResizeObserver();
      const { modalStage } = this.$refs;
      if (modalStage) {
        this.canvasResizeObserver = new ResizeObserver(this.throttleUpdateZoomContentStyle);
        this.canvasResizeObserver.observe(modalStage);
      }
    },
    destroyCanvasResizeObserver() {
      if (this.canvasResizeObserver) {
        const { modalStage } = this.$refs;
        if (modalStage) {
          this.canvasResizeObserver.unobserve(modalStage);
        }
        this.canvasResizeObserver = null;
      }
    },
    onResizeStart(resizeData) {
      const startCanvasHeight = this.modalCanvas?.options?.height
        || MODAL_CANVAS_HEIGHT;
      this.resizeData = {
        ...resizeData,
        startCanvasHeight,
        updatedCanvasHeight: startCanvasHeight,
      };
    },
    onResizeMove(resizeData) {
      if (!this.isResizing) return;
      const { moveY, startY } = resizeData;
      const { startCanvasHeight } = this.resizeData;
      const resizeDistance = ((moveY - startY) * 2) / this.scaleZoomed;
      const updatedCanvasHeight = Math.round(startCanvasHeight + resizeDistance);
      // Enforce a minimum and maximum height for the modal canvas.
      if (
        updatedCanvasHeight >= MODAL_CANVAS_MIN_HEIGHT
        && updatedCanvasHeight <= MODAL_CANVAS_MAX_HEIGHT
      ) {
        this.resizeData = {
          ...this.resizeData,
          ...resizeData,
          updatedCanvasHeight,
          canvasHeightChanged: updatedCanvasHeight !== startCanvasHeight,
        };
      }
    },
    onResizeEnd() {
      if (!this.isResizing) return;
      const { canvasHeightChanged } = this.resizeData;
      if (canvasHeightChanged) {
        const { updatedCanvasHeight } = this.resizeData;
        this[types.RESIZE_MODAL_CANVAS]({
          modalCanvasId: this.currentModalCanvasId,
          height: updatedCanvasHeight,
          width: this.modalCanvasWidth,
          fitModulesX: false,
        });
      }
      this.resizeData = null;
    },
    onResizeByIncrement(increment) {
      const updatedCanvasHeight = Math.round(this.modalCanvasHeight + increment);
      if (
        updatedCanvasHeight >= MODAL_CANVAS_MIN_HEIGHT
        && updatedCanvasHeight <= MODAL_CANVAS_MAX_HEIGHT
      ) {
        this[types.RESIZE_MODAL_CANVAS]({
          modalCanvasId: this.currentModalCanvasId,
          height: updatedCanvasHeight,
          width: this.modalCanvasWidth,
          fitModulesX: false,
        });
      }
    },
    resizeGridLayout() {
      this.$nextTick(() => {
        const { gridLayout } = this.$refs;
        if (gridLayout) {
          // Force the grid layout to update its width internally.
          gridLayout.onWindowResize();
        }
      });
    },
  },
  mounted() {
    bus.on('document:mousedown', this.onBodyClick);
    document.addEventListener('mousemove', this.updateMousePositionThrottled);
    this.initDropZone();
    this.updateZoomContentStyle();
    this.createCanvasResizeObserver();
    this.emitScaleZoomChanged();
    const { modalCanvasInnerWrap } = this.$refs;
    if (modalCanvasInnerWrap) {
      const zoomContentStyles = getComputedStyle(modalCanvasInnerWrap);
      this.canvasBorderWidth = parseInt(zoomContentStyles.borderWidth, 10);
    }
  },
  beforeUnmount() {
    bus.off('document:mousedown', this.onBodyClick);
    document.removeEventListener('mousemove', this.updateMousePositionThrottled);
    interact('.editor__contents').unset();
    this.destroyCanvasResizeObserver();
  },
};
</script>

<style lang="stylus">
.modal-canvas {
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  z-index: 1; // Underneath everything

  &--placeholder-hidden .vue-grid-placeholder {
    display: none;
  }

  &__toolbar {
    z-index: 800; // Above canvas
  }

  &__stage {
    bottom: 0;
    height: 100%;
    left: 0;
    overflow: auto;
    position: sticky;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 798;

    &--resizing {
      cursor: grabbing;
    }
  }

  &__zoom-spacer {
    left: 0;
    position: absolute;
    top: 0;
    transform-origin: 0 0;
    z-index: 799;
  }

  &__inner-wrap {
    // Match nebula-modal styles
    background-color: $nebula-color-white;
    border-radius: $nebula-border-radius-small;
    box-shadow: $nebula-shadow-400;
    left: 0;
    position: absolute;
    top: 0;
    transform-origin: 0 0;
    z-index: 799;

    &--mystery {
      // Match Mystery modal styles
      border: 7px solid #000000;
      border-radius: 0;
      box-shadow: none;
      box-sizing: content-box;
    }

    &--view-mode {
      overflow: hidden;
    }
  }

  &__resize-vertical {
    bottom: 0;
    left: 50%;
    position: absolute;
    transform-origin: center center;
    transition: opacity $nebula-transition-default;
    z-index: 2147483644; // Above all except module floating menus
  }

  &__x-button {
    $x-button-size = ($nebula-space-4x + $nebula-space-half);
    height: $x-button-size;
    inset-inline-end: $nebula-space-1x;
    position: absolute;
    top: $nebula-space-1x;
    width: $x-button-size;
    z-index: 800;

    .nebula-button__icon {
      height: $nebula-space-3x;
      width: $nebula-space-3x;
    }
  }

  &__click-target {
    width: 100%;
    height: 100%;
  }
}

html[dir="rtl"] & {
  .modal-canvas__inner-wrap {
    left: auto;
    right: 0;
    transform-origin: right top;
  }

  .modal-canvas__zoom-spacer {
    left: auto;
    right: 0;
    transform-origin: right top;
  }
}
</style>
